// AIT-529 — /invite/connect, the standalone channel picker an invited client
// sees straight after their account is created and the affiliate grant lands.
//
// Deliberately NOT the workspace + <ConnectChannelModal> pair the normal
// signup gets: these clients came for one thing (connecting their channel)
// and may never open a workspace at all, so the whole flow — pick, OAuth,
// done — happens on standalone screens in the invite's own visual language.
//
// The channel launchers are the same hooks the in-app modal uses
// (useConnectMetaSignup / useConnectInstagramLogin), so there is exactly one
// implementation of the Meta OAuth start path.

import { Loader2, ChevronRight } from 'lucide-react';
import { useFetch } from '@/hooks/use-fetch';
import { useSession } from '@/contexts/session-context';
import { useTrackReferralSignup } from '@/hooks/use-track-referral-signup';
import { useWorkspace } from '@/contexts/workspace-context';
import { useInstagramEnabled } from '@/hooks/use-app-config';
import { useConnectMetaSignup } from '@/components/use-connect-meta-signup';
import { useConnectInstagramLogin } from '@/components/use-connect-instagram-login';
import { InstagramGlyph, WhatsAppGlyph } from '@/components/connect-channel-modal';
import { emit } from '@/lib/analytics';
import { setInviteConnectPending } from '@/lib/invite-connect';
import { useCompleteInvite } from './use-complete-invite';
import { PageShell } from './page-shell';

export function InviteConnectPage() {
  const fetchWithAuth = useFetch();
  // Grant runs in the background; the picker never waits on it.
  useCompleteInvite();
  // The referral conversion the affiliate is paid for. This flow never mounts
  // RootRedirect, which is where it used to live (Codex review P1).
  const { user } = useSession();
  useTrackReferralSignup(user);
  const { activeWorkspace, workspaces, loading } = useWorkspace();
  const instagramEnabled = useInstagramEnabled();

  // A fresh signup owns exactly one workspace; activeWorkspace can still be
  // null on the first render after signup, so fall back to the list.
  const target = activeWorkspace?.id ?? workspaces[0]?.id;

  const connectWhatsApp = useConnectMetaSignup(target, fetchWithAuth);
  const connectInstagram = useConnectInstagramLogin(target, fetchWithAuth);

  const handleWhatsApp = async () => {
    setInviteConnectPending();
    await connectWhatsApp();
  };

  const handleInstagram = async () => {
    emit('instagram_connect_clicked', {});
    emit('instagram_login_clicked', {});
    setInviteConnectPending();
    await connectInstagram();
  };

  if (loading) {
    return (
      <PageShell>
        <Loader2
          role="status"
          aria-label="Loading"
          className="relative z-10 h-8 w-8 animate-spin text-white/70"
        />
      </PageShell>
    );
  }

  // No workspace to connect into. Rare (signup bootstraps one), but a member
  // added to an org that has none would land here. Never auto-redirect into
  // the app: this flow exists precisely so these clients don't get dropped
  // into a workspace they didn't ask for.
  if (!target) {
    return (
      <PageShell>
        <p role="status" className="relative z-10 max-w-sm text-center text-white/70">
          Your account is ready. There is no channel to connect yet, so you can close this tab.
        </p>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="relative z-10 w-full max-w-md rounded-[20px] border border-white/10 bg-white/[0.04] px-8 py-10 text-center shadow-[0_12px_28px_rgba(0,0,0,0.2),0_2px_4px_rgba(0,0,0,0.1)] backdrop-blur-sm">
        <img src="/logo-mark.svg" alt="" aria-hidden="true" className="mx-auto mb-6 h-14 w-auto" />
        <h1 className="text-2xl font-semibold text-white">Connect your channel</h1>

        <div className="mt-8 flex flex-col gap-3 text-left">
          <ChannelRow
            name="WhatsApp"
            icon={<WhatsAppGlyph />}
            iconTintClass="bg-[#25D366]/10"
            onClick={() => void handleWhatsApp()}
          />
          {instagramEnabled ? (
            <ChannelRow
              name="Instagram"
              icon={<InstagramGlyph />}
              iconTintClass="bg-gradient-to-br from-[#FA7E1E]/15 via-[#833AB4]/15 to-[#5851DB]/15"
              onClick={() => void handleInstagram()}
            />
          ) : null}
        </div>
      </div>
    </PageShell>
  );
}

function ChannelRow({
  name,
  icon,
  iconTintClass,
  onClick,
}: {
  name: string;
  icon: React.ReactNode;
  iconTintClass: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={name}
      className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] p-4 text-left text-white transition hover:border-white/25 hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0064E0]"
    >
      <span
        className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${iconTintClass}`}
      >
        {icon}
      </span>
      <span className="font-medium">{name}</span>
      <ChevronRight className="ml-auto size-4 text-white/50 transition group-hover:translate-x-0.5 group-hover:text-white" />
    </button>
  );
}
